//Built-in libraries

//Third party libraries
import { EllipsisVerticalIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import type React from 'react';
//Services
import { useDashboardWidgetListUpdate, useDashboardWidgetsList } from '@/hooks/ReactQueryHooks/useDashboardQuery';
//Assets and miscellaneous
import type { DashboardType } from '@/types/Analytics.ts';
import { HasPermission } from '../../Permission/HasPermission';
//Custom components
import type { DropDownMenuItem } from '../../UI/DropDown';
import SimpleDropDown, { DropDownItem } from '../../UI/SimpleDropDown';
import type { WidgetListInterface } from '../types';

interface WidgetLayoutProps {
  widget: string;
  dashboard: DashboardType;
  children: React.ReactNode;
  actionsBtnLocation?: string;
}

const WidgetLayout = ({ children, widget, dashboard, actionsBtnLocation = 'top-4 right-2' }: WidgetLayoutProps) => {
  const { fetchDashboardWidgets } = useDashboardWidgetsList({
    dashboard,
  });
  const dashboardWidgets = fetchDashboardWidgets?.data;
  const widgetList = dashboardWidgets?.[0]?.widgets;
  const dashboardType = dashboardWidgets?.[0]?.slug;
  const updateDashboardList = useDashboardWidgetListUpdate({
    dashboard,
  });
  const postUpdatedDashboard = (name: string) => {
    const widgets = widgetList.map((wdg: WidgetListInterface) => {
      if (wdg.slug === name) {
        return { ...wdg, isUsed: !wdg.isUsed };
      }
      return wdg;
    });
    const usedWidgetsList = widgets
      .filter((wdg: WidgetListInterface) => wdg.isUsed === true)
      .map((wdgt: WidgetListInterface) => ({ widgetId: wdgt.id }));

    const finalData = {
      widgets: usedWidgetsList,
      dashboardType: dashboardType,
    };
    updateDashboardList.mutate(finalData);
  };

  const dropdownItems: DropDownMenuItem[] = [
    {
      name: 'Remove',
      clicked: () => postUpdatedDashboard(widget),
      icon: <EyeSlashIcon className='h-5 w-5' />,
      permission: { of: 'MANAGE_WIDGETS' },
    },
  ];

  return (
    <div className='relative h-auto w-full min-w-0'>
      <HasPermission of='MANAGE_WIDGETS'>
        <div
          className={`z-10 absolute rounded-full p-1 text-neutralColor-normal cursor-pointer hover:bg-neutralColor-light
        transition duration-300 ease-in-out ${actionsBtnLocation}`}
        >
          <SimpleDropDown toggle={<EllipsisVerticalIcon className='w-5 h-5' />}>
            {dropdownItems.map((item) => (
              <DropDownItem id={item.name} key={item.name} onClick={item.clicked}>
                <span>{item.icon}</span>
                <span>{item.name}</span>
              </DropDownItem>
            ))}
          </SimpleDropDown>
        </div>
      </HasPermission>
      {children}
    </div>
  );
};

export default WidgetLayout;
